import {
  Label,
  Input,
  Combobox,
  Textarea,
  Option,
} from '@fluentui/react-components';
import React, { ChangeEvent } from 'react';
import { CancellIconLarge } from 'src/components/Goals/GoalCard/style/GoalCard.styled';
import { Alert } from 'src/components/common/AlertMessage/AlertMessage';
import { Employee } from 'src/tabs/hooks/useGetAllUsers';
import { FeedbackTopic } from 'src/tabs/models/FeedbackInterfaces';
import {
  CompanyValueButton,
  CompanyValuesContainer,
  EmployeeSuggestionsFeedback,
  FeedbackField,
  LabelIconContainer,
  FeedbackForm as FeedbackFormContainer,
} from 'src/tabs/styles/Feedback.styled';
import {
  ErrorCircle16Filled,
  Search20Regular as SearchIcon,
} from '@fluentui/react-icons';
import { isMobile } from 'react-device-detect';

interface FeedbackFormProps {
  formErrors: {
    topic: string;
    employee: string;
    feedback: string;
    companyValues: string;
  };
  requestedBy: string;
  feedbackEmptyState: boolean;
  fieldRef: React.Ref<HTMLDivElement>;
  isFormValid: string;
  feedbackTopics: FeedbackTopic[];
  selectedTopic: string;
  comboboxWidth: number;
  selectedFeedbackId: string;
  employee: string;
  companyValuesIconsMapped: {
    icon: string;
    label: string;
    value: string;
  }[];
  selectedCompanyValues: string[];
  feedbackTextArea: string;
  filteredUsers: Employee[];
  selectedEmployeeId: string;
  setSelectedCompanyValues: React.Dispatch<React.SetStateAction<string[]>>;
  setFormErrors: React.Dispatch<
    React.SetStateAction<{
      topic: string;
      employee: string;
      feedback: string;
      companyValues: string;
    }>
  >;
  setFeedbackTextArea: React.Dispatch<React.SetStateAction<string>>;
  setSelectedEmployeeValues: React.Dispatch<
    React.SetStateAction<{
      requestedBy: string;
      employee: string;
    }>
  >;
  setSelectedEmployeeId: React.Dispatch<React.SetStateAction<string>>;
  setSelectedTopic: React.Dispatch<React.SetStateAction<string>>;
  currentUser: Employee;
}

const feedBackAboutMyselfTopicId = '1be21a65-4735-eb11-a813-000d3ad9719b';

export const FeedbackForm = ({
  formErrors,
  requestedBy,
  feedbackEmptyState,
  fieldRef,
  isFormValid,
  feedbackTopics,
  selectedTopic,
  comboboxWidth,
  selectedFeedbackId,
  employee,
  companyValuesIconsMapped,
  selectedCompanyValues,
  feedbackTextArea,
  filteredUsers,
  selectedEmployeeId,
  setSelectedCompanyValues,
  setFormErrors,
  setFeedbackTextArea,
  setSelectedEmployeeValues,
  setSelectedEmployeeId,
  setSelectedTopic,
  currentUser,
}: FeedbackFormProps) => {
  const handleTextAreaResize = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const element = e.target;

    element.style.height = '0px';

    const scrollHeight = element.scrollHeight;

    element.style.height = `${scrollHeight}px`;
  };

  return (
    <FeedbackFormContainer>
      {isFormValid && (
        <Alert
          requestStatusAppearance={'error'}
          requestStatusMessage={
            formErrors.topic ||
            formErrors.employee ||
            formErrors.feedback ||
            formErrors.companyValues
          }
          showCloseButton={false}
          handleClose={null}
        />
      )}
      {!feedbackEmptyState && (
        <FeedbackField>
          <Label>Requested by</Label>
          <Input
            appearance="filled-lighter-shadow"
            size="medium"
            value={requestedBy}
            readOnly
          />
        </FeedbackField>
      )}
      <FeedbackField
        ref={fieldRef}
        validationState={formErrors.topic ? 'error' : 'none'}
      >
        <LabelIconContainer>
          {feedbackEmptyState && formErrors.topic && (
            <ErrorCircle16Filled color="#B10E1C" />
          )}
          <Label required={feedbackEmptyState}>Feedback topic</Label>
        </LabelIconContainer>
        {feedbackEmptyState ? (
          <Combobox
            placeholder="Select your topic"
            appearance="filled-darker"
            size="medium"
            value={
              feedbackTopics?.find(
                (topic) => topic.cra0e_feedbacktopicid === selectedTopic
              )?.cra0e_name || ''
            }
            listbox={{
              style: {
                height: 110,
                width: !isMobile ? comboboxWidth : '92.5%',
              },
            }}
          >
            {feedbackTopics?.map((topic) => (
              <Option
                checkIcon={null}
                key={topic.cra0e_feedbacktopicid}
                value={topic.cra0e_name}
                onClick={() => {
                  setSelectedTopic(topic.cra0e_feedbacktopicid);
                  setFormErrors((prev) => ({
                    ...prev,
                    topic: '',
                  }));
                  if (
                    topic.cra0e_feedbacktopicid === feedBackAboutMyselfTopicId
                  ) {
                    setSelectedEmployeeValues((prev) => ({
                      ...prev,
                      employee: currentUser.new_name,
                    }));
                    setSelectedEmployeeId(currentUser.new_employeeid);
                    setFormErrors((prev) => ({
                      ...prev,
                      employee: '',
                    }));
                  } else {
                    setSelectedEmployeeValues((prev) => ({
                      ...prev,
                      employee: '',
                    }));
                    setSelectedEmployeeId('');
                  }
                }}
              >
                {topic.cra0e_name}
              </Option>
            ))}
          </Combobox>
        ) : (
          <Input
            appearance="filled-lighter-shadow"
            size="medium"
            value={
              feedbackTopics?.find(
                (topic) => topic.cra0e_feedbacktopicid === selectedTopic
              )?.cra0e_name || ''
            }
            readOnly
          />
        )}
      </FeedbackField>
      <FeedbackField validationState={formErrors.employee ? 'error' : 'none'}>
        <LabelIconContainer>
          {feedbackEmptyState && formErrors.employee && (
            <ErrorCircle16Filled color="#B10E1C" />
          )}
          <Label required={feedbackEmptyState}>Employee</Label>
        </LabelIconContainer>
        <Input
          placeholder="Search person by name..."
          appearance={
            !feedbackEmptyState || selectedTopic === feedBackAboutMyselfTopicId
              ? 'filled-lighter-shadow'
              : 'filled-darker'
          }
          contentAfter={
            !selectedFeedbackId &&
            selectedTopic !== feedBackAboutMyselfTopicId ? (
              !employee ? (
                <SearchIcon
                  style={{
                    cursor: 'pointer',
                  }}
                />
              ) : (
                <CancellIconLarge
                  style={{
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setSelectedEmployeeId('');
                    setSelectedEmployeeValues((prev) => ({
                      ...prev,
                      employee: '',
                    }));
                  }}
                />
              )
            ) : null
          }
          size="medium"
          value={employee}
          onChange={(e) => {
            if (selectedEmployeeId) {
              setSelectedEmployeeId('');
            }
            setSelectedEmployeeValues((prev) => ({
              ...prev,
              employee: e.target.value,
            }));
          }}
          readOnly={
            !feedbackEmptyState || selectedTopic === feedBackAboutMyselfTopicId
          }
        />
        {!selectedFeedbackId &&
          employee &&
          !selectedEmployeeId &&
          filteredUsers.length > 0 && (
            <EmployeeSuggestionsFeedback>
              {filteredUsers?.map((user) => (
                <p
                  key={user.new_employeeid}
                  onClick={() => {
                    setSelectedEmployeeValues((prev) => ({
                      ...prev,
                      employee: user.new_name,
                    }));
                    setSelectedEmployeeId(user.new_employeeid);
                    setFormErrors((prev) => ({
                      ...prev,
                      employee: '',
                    }));
                  }}
                >
                  {user.new_name}
                </p>
              ))}
            </EmployeeSuggestionsFeedback>
          )}
      </FeedbackField>
      <FeedbackField
        validationMessageIcon={null}
        validationState={formErrors.feedback ? 'error' : 'none'}
      >
        <LabelIconContainer>
          {formErrors.feedback && <ErrorCircle16Filled color="#B10E1C" />}
          <Label required>Feedback</Label>
        </LabelIconContainer>
        <Textarea
          onChange={(e) => {
            handleTextAreaResize(e);
            setFeedbackTextArea(e.target.value);
            setFormErrors((prev) => ({
              ...prev,
              feedback: '',
            }));
          }}
          value={feedbackTextArea}
          appearance="filled-darker"
          placeholder="Please type your feedback here."
        />
      </FeedbackField>
      <FeedbackField
        validationState={formErrors.companyValues ? 'error' : 'none'}
      >
        <LabelIconContainer>
          <Label>Praise for</Label>
        </LabelIconContainer>
        <CompanyValuesContainer>
          {companyValuesIconsMapped?.map((value) => (
            <CompanyValueButton
              key={value.value}
              selected={selectedCompanyValues?.includes(value.value)}
              onClick={() =>
                setSelectedCompanyValues((prev) => {
                  if (prev.includes(value.value)) {
                    return prev.filter((val) => val !== value.value);
                  }
                  setFormErrors((prev) => ({
                    ...prev,
                    companyValues: '',
                  }));
                  return [...prev, value.value];
                })
              }
              icon={<img src={value.icon} />}
            >
              {value.label}
            </CompanyValueButton>
          ))}
        </CompanyValuesContainer>
      </FeedbackField>
    </FeedbackFormContainer>
  );
};
